import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post, patch, del } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { EmptyState } from "@/components/common/EmptyState";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { GripVertical, Plus, Pencil, Trash2, Wallet, Info } from "lucide-react";
import { toast } from "sonner";

export function PaymentTermsTab({ lead }) {
  const { can } = useApp();
  const qc = useQueryClient();
  const [modal, setModal] = useState(null);
  const [drag, setDrag] = useState(null);

  const { data: terms = [], isLoading } = useQuery({
    queryKey: ["payment-terms", lead.id], queryFn: () => get(`/crm/leads/${lead.id}/payment-terms`),
  });
  const { data: quotations = [] } = useQuery({
    queryKey: ["quotations", lead.id], queryFn: () => get(`/crm/quotations?lead_id=${lead.id}`),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["payment-terms", lead.id] });

  const create = useMutation({ mutationFn: (body) => post(`/crm/leads/${lead.id}/payment-terms`, body), onSuccess: () => { invalidate(); setModal(null); } });
  const update = useMutation({ mutationFn: ({ id, body }) => patch(`/crm/payment-terms/${id}`, body), onSuccess: () => { invalidate(); setModal(null); } });
  const remove = useMutation({ mutationFn: (id) => del(`/crm/payment-terms/${id}`), onSuccess: () => { invalidate(); toast.success("Term deleted"); } });
  const reorder = useMutation({ mutationFn: (order) => post(`/crm/leads/${lead.id}/payment-terms/reorder`, { order }), onSuccess: invalidate });

  const quoTotal = (qid) => {
    const q = quotations.find((x) => x.id === qid);
    if (!q) return 0;
    return (q.line_items || []).reduce((s, li) => s + li.qty * li.rate * (1 - (li.discount || 0) / 100) * (1 + (li.tax || 0) / 100), 0);
  };

  const sorted = [...terms].sort((a, b) => a.order_index - b.order_index);
  const pctTotal = sorted.reduce((s, t) => s + (Number(t.percentage) || 0), 0);

  if (isLoading) return null;

  return (
    <div>
      {can("crm", "create") && (
        <Button size="sm" variant="outline" className="mb-3" data-testid="add-payment-term-btn" onClick={() => setModal({ mode: "new" })}>
          <Plus className="mr-1 h-3.5 w-3.5" />Add Term
        </Button>
      )}
      {sorted.length === 0 ? <EmptyState icon={Wallet} title="No payment terms yet" /> : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <table className="w-full text-sm" data-testid="payment-terms-table">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="w-8"></th>
                <th className="px-3 py-2.5 font-semibold">Milestone</th>
                <th className="px-3 py-2.5 text-right font-semibold">%</th>
                <th className="px-3 py-2.5 text-right font-semibold">Amount</th>
                <th className="px-3 py-2.5 font-semibold">Due Condition</th>
                <th className="px-3 py-2.5 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((t) => {
                const linked = !!t.quotation_id;
                const amount = linked ? quoTotal(t.quotation_id) * ((t.percentage || 0) / 100) : t.amount;
                return (
                  <tr key={t.id} data-testid={`payment-term-row-${t.id}`}
                    draggable={can("crm", "edit")} onDragStart={() => setDrag(t.id)} onDragOver={(e) => e.preventDefault()}
                    onDrop={() => { if (drag && drag !== t.id) { const ids = sorted.map((x) => x.id); const from = ids.indexOf(drag), to = ids.indexOf(t.id); ids.splice(to, 0, ids.splice(from, 1)[0]); reorder.mutate(ids); } setDrag(null); }}
                    className={cn("border-b border-border last:border-0 hover:bg-muted/50", drag === t.id && "opacity-50")}>
                    <td className="pl-2"><GripVertical className="h-4 w-4 text-muted-foreground" /></td>
                    <td className="px-3 py-2.5 font-medium">{t.milestone_name}</td>
                    <td className="px-3 py-2.5 text-right">{t.percentage}%</td>
                    <td className="px-3 py-2.5 text-right">
                      <span className="inline-flex items-center gap-1">
                        {money(amount, lead.currency)}
                        {linked && <Info className="h-3 w-3 text-muted-foreground" title="Auto-calculated from linked quotation" />}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-muted-foreground">{t.due_condition}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center justify-end gap-1">
                        {can("crm", "edit") && (
                          <Button variant="ghost" size="icon" data-testid={`edit-term-${t.id}`} onClick={() => setModal({ mode: "edit", data: t })}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {can("crm", "delete") && (
                          <ConfirmDelete testid={`del-term-${t.id}`} title={`Delete "${t.milestone_name}"?`}
                            trigger={<Button variant="ghost" size="icon"><Trash2 className="h-3.5 w-3.5" /></Button>}
                            onConfirm={() => remove.mutate(t.id)} />
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className={cn("border-t border-border px-3 py-2 text-right text-sm font-medium",
            pctTotal === 100 ? "text-emerald-600" : "text-amber-600")} data-testid="payment-terms-total">
            {pctTotal === 100 ? `Total: 100%` : `Total: ${pctTotal}% — add ${100 - pctTotal}% more`}
          </div>
        </div>
      )}

      <TermModal state={modal} setState={setModal} quotations={quotations}
        onSubmit={(body) => modal?.mode === "new" ? create.mutate(body) : update.mutate({ id: modal.data.id, body })} />
    </div>
  );
}

function TermModal({ state, setState, quotations, onSubmit }) {
  const [form, setForm] = useState({});
  const d = state?.data || {};
  const val = (k) => (form[k] !== undefined ? form[k] : d[k]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <Dialog open={!!state} onOpenChange={(v) => { if (!v) setState(null); else setForm({}); }} key={d.id || "new-term"}>
      <DialogContent className="max-w-md" data-testid="payment-term-modal">
        <DialogHeader><DialogTitle>{state?.mode === "edit" ? "Edit Term" : "Add Payment Term"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Milestone Name *</Label><Input data-testid="term-name" className="mt-1" value={val("milestone_name") || ""} onChange={(e) => set("milestone_name", e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Percentage *</Label><Input type="number" data-testid="term-pct" className="mt-1" value={val("percentage") ?? ""} onChange={(e) => set("percentage", Number(e.target.value))} /></div>
            <div>
              <Label>Linked Quotation</Label>
              <Select value={val("quotation_id") || "none"} onValueChange={(v) => set("quotation_id", v === "none" ? null : v)}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {quotations.map((q) => <SelectItem key={q.id} value={q.id}>v{q.version}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {!val("quotation_id") && (
            <div><Label>Amount (manual)</Label><Input type="number" data-testid="term-amount" className="mt-1" value={val("amount") ?? ""} onChange={(e) => set("amount", Number(e.target.value))} /></div>
          )}
          <div><Label>Due Condition</Label><Input data-testid="term-due" className="mt-1" placeholder="e.g. Within 7 days of signing" value={val("due_condition") || ""} onChange={(e) => set("due_condition", e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setState(null)}>Cancel</Button>
          <Button data-testid="term-submit" onClick={() => {
            if (!val("milestone_name") || val("percentage") == null) { toast.error("Milestone name and percentage are required"); return; }
            onSubmit({ milestone_name: val("milestone_name"), percentage: val("percentage"), quotation_id: val("quotation_id") || null, amount: val("amount") || 0, due_condition: val("due_condition") || "" });
          }}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
